import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  SimpleGrid,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
  MCP: 'cyan',
};

const ApiDocs = () => {
  const chatEndpoints = [
    {
      title: 'Send a chat message',
      method: 'POST',
      path: '/api/v1/chat/message',
      description: 'Send a message to the assistant. Streams the reply back as newline-delimited JSON. tenant_id is required to start a new session (session stays scoped to that tenant for its lifetime); later turns only need session_id. Authenticated via a DRF Token (standalone chat UI) or the shared X-Internal-Key header (server-to-server, from api/control\'s embedded assistant proxy).',
      request: `{
  "session_id": 12,       // omit to start a new session
  "tenant_id": 3,         // required when session_id is omitted
  "message": "What events do I have tomorrow?"
}`,
      response: `// newline-delimited JSON, one object per line:
{"type": "session", "session_id": 12}
{"type": "token", "text": "You "}
{"type": "token", "text": "have "}
{"type": "references", "events": [{"id": 5, "name": "..."}]}
{"type": "done"}`
    },
    {
      title: 'List tenants',
      method: 'GET',
      path: '/api/v1/tenants',
      description: 'List active instances available to query, for the chat UI\'s instance picker. Proxies api/control\'s instance list.',
      response: `{
  "tenants": [
    {"id": 1, "name": "Acme Events"},
    {"id": 3, "name": "Example Org"}
  ]
}`
    },
  ];

  const authEndpoints = [
    {
      title: 'Login',
      method: 'POST',
      path: '/api/v1/auth/login/',
      description: 'Exchange a username/password for a DRF auth token, used as `Authorization: Token <token>` on subsequent requests.',
      request: `{
  "username": "admin",
  "password": "changeme"
}`,
      response: `{
  "token": "9944b09199c62bcf9418ad846dd0e4bbdfc6ee4"
}`
    },
    {
      title: 'Liveness ping',
      method: 'GET',
      path: '/api/v1/ping',
      description: 'Unauthenticated liveness check. Always returns 200 if the process is up.',
      response: `{
  "status": "ok"
}`
    },
  ];

  const userEndpoints = [
    {
      title: 'List users',
      method: 'GET',
      path: '/api/v1/users/',
      description: 'List every user who can log into this admin UI. Everyone has equal access — there\'s no separate admin role to grant.',
      response: `[
  {
    "id": 1,
    "username": "admin",
    "email": "admin@selwyn.local",
    "is_active": true,
    "date_joined": "2026-01-01T00:00:00Z"
  }
]`
    },
    {
      title: 'Create user',
      method: 'POST',
      path: '/api/v1/users/',
      description: 'Create a new user. Password is validated against Django\'s standard password validators (length, similarity to username, common-password check).',
      request: `{
  "username": "newperson",
  "email": "new@example.com",
  "password": "..."
}`,
      response: `{
  "id": 2,
  "username": "newperson",
  "email": "new@example.com",
  "is_active": true,
  "date_joined": "2026-08-03T12:00:00Z"
}`
    },
    {
      title: 'Update user',
      method: 'PUT',
      path: '/api/v1/users/:id/',
      description: 'Update a user\'s username, email, active status, or password. Omit password to leave it unchanged. A user editing their own account this way is how self-service password changes work — there\'s no separate "change my password" endpoint. A user cannot deactivate their own account (to avoid self-lockout).',
      request: `{
  "username": "newperson",
  "email": "new@example.com",
  "password": "..."   // optional — omit to keep the current password
}`,
      response: `{
  "id": 2,
  "username": "newperson",
  "email": "new@example.com",
  "is_active": true,
  "date_joined": "2026-08-03T12:00:00Z"
}`
    },
    {
      title: 'Delete user',
      method: 'DELETE',
      path: '/api/v1/users/:id/',
      description: 'Delete a user. A user cannot delete their own account.',
      response: `{
  "success": true
}`
    },
    {
      title: 'Get current user',
      method: 'GET',
      path: '/api/v1/users/me/',
      description: 'The current authenticated user\'s own info — used by the UI to mark "you" in the users list and disable self-delete/self-deactivate.',
      response: `{
  "id": 1,
  "username": "admin",
  "email": "admin@selwyn.local",
  "is_active": true,
  "date_joined": "2026-01-01T00:00:00Z"
}`
    },
  ];

  const usageEndpoints = [
    {
      title: 'Usage by tenant',
      method: 'GET',
      path: '/api/v1/usage/?days=30',
      description: 'Aggregate LLM call counts and token usage per tenant, broken down by provider/model within each tenant, over a configurable window (max 365 days). Recorded by agent.py once per LLM call — a single user turn can produce more than one row when the model makes tool calls before its final answer.',
      response: `{
  "period_days": 30,
  "tenants": [
    {
      "tenant_id": 3,
      "tenant_name": "Acme Events",
      "total_calls": 120,
      "total_input_tokens": 45000,
      "total_output_tokens": 12000,
      "by_model": [
        {"provider": "ollama", "model": "qwen2.5:3b-instruct-q4_K_M", "calls": 100, "input_tokens": 38000, "output_tokens": 9500},
        {"provider": "managed", "model": "kimi-k2-instruct", "calls": 20, "input_tokens": 7000, "output_tokens": 2500}
      ]
    }
  ],
  "totals": {"total_calls": 120, "total_input_tokens": 45000, "total_output_tokens": 12000}
}`
    },
    {
      title: 'List discussions',
      method: 'GET',
      path: '/api/v1/discussions/?tenant_id=&search=&page=1',
      description: 'List chat sessions ("discussions"), newest first. tenant_id filters to one tenant; search matches message content (ILIKE-style, case-insensitive).',
      response: `{
  "results": [
    {
      "id": 12,
      "tenant_id": 3,
      "tenant_name": "Acme Events",
      "created_at": "2026-08-01T10:00:00Z",
      "updated_at": "2026-08-01T10:05:00Z",
      "message_count": 4,
      "last_message_preview": "You have Annual Gala tomorrow at 6pm."
    }
  ],
  "pagination": {"page": 1, "per_page": 25, "total": 1, "pages": 1}
}`
    },
    {
      title: 'Get discussion',
      method: 'GET',
      path: '/api/v1/discussions/:id/',
      description: 'Full transcript for one discussion (every message, including tool calls), plus the LLM usage its calls incurred.',
      response: `{
  "id": 12,
  "tenant_id": 3,
  "tenant_name": "Acme Events",
  "created_at": "2026-08-01T10:00:00Z",
  "updated_at": "2026-08-01T10:05:00Z",
  "messages": [
    {"id": 1, "role": "user", "content": "What events do I have tomorrow?", "tool_name": null, "created_at": "2026-08-01T10:00:00Z"},
    {"id": 2, "role": "tool", "content": "{...}", "tool_name": "get_upcoming_events", "created_at": "2026-08-01T10:00:01Z"},
    {"id": 3, "role": "assistant", "content": "You have Annual Gala tomorrow at 6pm.", "tool_name": null, "created_at": "2026-08-01T10:00:03Z"}
  ],
  "usage": [
    {"provider": "ollama", "model": "qwen2.5:3b-instruct-q4_K_M", "calls": 2, "input_tokens": 900, "output_tokens": 120}
  ]
}`
    },
  ];

  const mcpKeyEndpoints = [
    {
      title: 'List MCP API keys',
      method: 'GET',
      path: '/api/v1/mcp-keys/',
      description: 'List every tenant-scoped MCP API key. The full key value is never included here — only a masked preview.',
      response: `[
  {
    "id": 1,
    "tenant_id": 3,
    "tenant_name": "Acme Events",
    "name": "Claude Desktop",
    "is_active": true,
    "key_masked": "****************ab12",
    "created_at": "2026-08-01T10:00:00Z",
    "last_used_at": "2026-08-03T09:15:00Z"
  }
]`
    },
    {
      title: 'Create MCP API key',
      method: 'POST',
      path: '/api/v1/mcp-keys/',
      description: 'Create a new tenant-scoped key. The full key is only ever returned in this response — copy it now, it can\'t be retrieved again (only revoked and replaced).',
      request: `{
  "tenant_id": 3,
  "name": "Claude Desktop"
}`,
      response: `{
  "id": 1,
  "tenant_id": 3,
  "tenant_name": "Acme Events",
  "name": "Claude Desktop",
  "is_active": true,
  "key": "AbCdEf123...",
  "key_masked": "****************ab12",
  "created_at": "2026-08-01T10:00:00Z",
  "last_used_at": null
}`
    },
    {
      title: 'Update MCP API key',
      method: 'PUT',
      path: '/api/v1/mcp-keys/:id/',
      description: 'Rename or activate/deactivate a key. The tenant and key value can\'t be changed once created — issue a new key instead.',
      request: `{
  "name": "Claude Desktop (renamed)",
  "is_active": false
}`,
      response: `{
  "id": 1,
  "tenant_id": 3,
  "tenant_name": "Acme Events",
  "name": "Claude Desktop (renamed)",
  "is_active": false,
  "key_masked": "****************ab12",
  "created_at": "2026-08-01T10:00:00Z",
  "last_used_at": "2026-08-03T09:15:00Z"
}`
    },
    {
      title: 'Delete MCP API key',
      method: 'DELETE',
      path: '/api/v1/mcp-keys/:id/',
      description: 'Permanently revoke a key. Any client using it loses access immediately.',
      response: `{
  "success": true
}`
    },
  ];

  const mcpTools = [
    {
      title: 'get_upcoming_events',
      method: 'MCP',
      path: 'get_upcoming_events',
      description: 'List this instance\'s events starting within a requested window, including name, start date/time, and registration counts. Provide exactly one of range, months_ahead, or start_date+end_date.',
      request: `{
  "range": "tomorrow",        // one of: today, tomorrow, this_week, next_7_days
  "months_ahead": 2,          // integer — alternative to range
  "start_date": "2026-08-10", // "YYYY-MM-DD" — alternative to range, used with end_date
  "end_date": "2026-08-17"
}`,
      response: `{
  "range": "tomorrow",
  "events": [
    {
      "id": 42,
      "name": "Annual Gala",
      "start_date": "2026-08-04",
      "start_time": "18:00:00",
      "duration_minutes": 180,
      "registration_count": 120,
      "registrations_remaining": 30
    }
  ]
}`
    },
    {
      title: 'get_registration_counts',
      method: 'MCP',
      path: 'get_registration_counts',
      description: 'Look up how many people have signed up for a specific event, by name (fuzzy match) or numeric event instance id. Merges RSVP-style registrations with paid ticketing counts when both exist for an event.',
      request: `{
  "event_name_or_id": "Annual Gala"
}`,
      response: `{
  "query": "Annual Gala",
  "matches": [
    {
      "id": 42,
      "name": "Annual Gala",
      "start_date": "2026-08-04",
      "registration_count": 120,
      "registrations_remaining": 30,
      "tickets_sold": 95,
      "revenue": 4750.0
    }
  ]
}`
    },
    {
      title: 'get_rundown_gap_events',
      method: 'MCP',
      path: 'get_rundown_gap_events',
      description: 'List events in the requested window whose Event Planner rundown doesn\'t yet fill the event\'s scheduled duration. Same window parameters as get_upcoming_events.',
      request: `{
  "range": "tomorrow"
}`,
      response: `{
  "range": "tomorrow",
  "events_with_gaps": [
    {
      "id": 42,
      "name": "Annual Gala",
      "start_date": "2026-08-04",
      "planned_minutes": 120,
      "budget_minutes": 180,
      "gap_minutes": 60,
      "status": "gap"
    }
  ]
}`
    },
    {
      title: 'get_waitlist_status',
      method: 'MCP',
      path: 'get_waitlist_status',
      description: 'Waitlist count for one event, broken down by priority tier, by name (partial match ok) or event instance id.',
      request: `{ "event_name_or_id": "Annual Gala" }`,
      response: `{
  "event": "Annual Gala",
  "event_instance_id": 42,
  "waitlist_count": 8,
  "by_priority": { "high": 2, "medium": 5, "low": 1 },
  "already_registered_count": 0
}`
    },
    {
      title: 'get_event_hosts_and_venue',
      method: 'MCP',
      path: 'get_event_hosts_and_venue',
      description: 'Venue details and assigned hosts for one event, by name (partial match ok) or event instance id.',
      request: `{ "event_name_or_id": "Annual Gala" }`,
      response: `{
  "event": "Annual Gala",
  "event_instance_id": 42,
  "venue": { "name": "The Grand Hall", "city": "Stirling", "postcode": "FK8 1AA" },
  "hosts": [{ "name": "Jane Doe", "email": "jane@example.com", "role": "Lead Host" }]
}`
    },
    {
      title: 'get_cart_activity',
      method: 'MCP',
      path: 'get_cart_activity',
      description: 'Shopping cart activity (a signal for checkout abandonment) in a date window, broken down by event. Same window parameters as get_upcoming_events.',
      request: `{ "range": "this_week" }`,
      response: `{
  "range": "this_week",
  "cart_count": 14,
  "priority_waitlist_count": 2,
  "by_event": [{ "event": "Annual Gala", "count": 9 }, { "event": "Summit", "count": 5 }]
}`
    },
    {
      title: 'get_ticket_revenue',
      method: 'MCP',
      path: 'get_ticket_revenue',
      description: 'Ticket sales and revenue for a ticketed event, by name (partial match ok) or event id.',
      request: `{ "event_name_or_id": "Annual Gala" }`,
      response: `{
  "query": "Annual Gala",
  "matches": [{ "id": 42, "name": "Annual Gala", "ticket_price": 20.0, "tickets_sold": 100, "ticket_quantity": 150, "revenue": 2000.0 }]
}`
    },
    {
      title: 'get_coupon_usage',
      method: 'MCP',
      path: 'get_coupon_usage',
      description: 'Redemption stats for a discount coupon, by code (partial match ok).',
      request: `{ "code": "SUMMER25" }`,
      response: `{
  "query": "SUMMER25",
  "matches": [{ "code": "SUMMER25", "type": "percentage", "value": 25, "uses": 10, "max_uses": 100, "active": true }]
}`
    },
    {
      title: 'get_profile_credits',
      method: 'MCP',
      path: 'get_profile_credits',
      description: 'Credit balance and transaction history for one profile. Requires the numeric profile id — not searchable by name.',
      request: `{ "profile_id": 482 }`,
      response: `{
  "profile_id": 482,
  "balance": 27.5,
  "transaction_count": 3,
  "transactions": [{ "amount": "10.00", "type": "bonus", "created_at": "2026-01-01T00:00:00Z" }]
}`
    },
    {
      title: 'get_upcoming_course_sessions',
      method: 'MCP',
      path: 'get_upcoming_course_sessions',
      description: 'List course sessions starting within a requested window, with enrollment counts. Same window parameters as get_upcoming_events.',
      request: `{ "range": "next_7_days" }`,
      response: `{
  "range": "next_7_days",
  "sessions": [{ "id": 5, "course": "Intro to Python", "start_date": "2026-08-10", "enrollment_count": 12, "available_spots": 8, "is_full": false }]
}`
    },
    {
      title: 'get_course_enrollment_counts',
      method: 'MCP',
      path: 'get_course_enrollment_counts',
      description: 'Enrollment count for one course session, by course/session name (partial match ok) or session id.',
      request: `{ "course_or_session_name_or_id": "Python" }`,
      response: `{
  "query": "Python",
  "matches": [{ "id": 5, "course": "Intro to Python", "enrollment_count": 12, "available_spots": 8, "is_full": false }]
}`
    },
    {
      title: 'get_course_catalog',
      method: 'MCP',
      path: 'get_course_catalog',
      description: 'Search the course catalog by title. Omit query to list every course.',
      request: `{ "query": "leadership" }`,
      response: `{
  "query": "leadership",
  "courses": [{ "id": 3, "title": "Leadership Fundamentals", "categories": ["Professional Development"], "session_count": 4, "full_price": 250.0 }]
}`
    },
    {
      title: 'get_enquiry_backlog',
      method: 'MCP',
      path: 'get_enquiry_backlog',
      description: 'Open/in-progress/resolved support enquiry counts, broken down by status and topic. No parameters.',
      response: `{
  "by_status": { "open": 3, "in_progress": 1, "resolved": 20 },
  "by_topic": { "Payment": 2, "Technical Issue": 1 },
  "total": 24
}`
    },
    {
      title: 'get_group_activity',
      method: 'MCP',
      path: 'get_group_activity',
      description: 'Membership count and recent discussion activity for a community group, by name (partial match ok) or id.',
      request: `{ "group_name_or_id": "Marketing Circle" }`,
      response: `{
  "query": "Marketing Circle",
  "matches": [{ "id": 1, "name": "Marketing Circle", "member_count": 40, "discussion_count": 3, "recent_discussions": [{ "title": "Q3 planning", "post_count": 5 }] }]
}`
    },
    {
      title: 'get_broadcast_stats',
      method: 'MCP',
      path: 'get_broadcast_stats',
      description: 'Delivery stats for broadcast email campaigns, by name/subject (partial match). Omit query to list every campaign.',
      request: `{ "query": "Newsletter" }`,
      response: `{
  "query": "Newsletter",
  "broadcasts": [{ "id": 1, "name": "August Newsletter", "status": "sent", "recipient_count": 500, "sent_at": "2026-08-01T00:00:00Z" }]
}`
    },
    {
      title: 'get_email_backlog',
      method: 'MCP',
      path: 'get_email_backlog',
      description: 'Count of email threads with unread inbound messages awaiting a reply. No parameters.',
      response: `{
  "total_threads": 40,
  "unread_thread_count": 6,
  "total_unread_messages": 9
}`
    },
    {
      title: 'get_sms_activity',
      method: 'MCP',
      path: 'get_sms_activity',
      description: 'SMS send counts in a date window. Same window parameters as get_upcoming_events.',
      request: `{ "range": "this_week" }`,
      response: `{
  "range": "this_week",
  "total_count": 120,
  "sent_count": 115
}`
    },
    {
      title: 'get_membership_overview',
      method: 'MCP',
      path: 'get_membership_overview',
      description: 'Total/active/inactive/enrolled membership counts, broken down by membership type and region. No parameters.',
      response: `{
  "total": 5, "active": 4, "inactive": 1, "enrolled": 120,
  "by_type": [{ "type": "individual", "count": 100 }],
  "by_region": [{ "region": "Scotland", "count": 80 }]
}`
    },
    {
      title: 'get_profile_segment_counts',
      method: 'MCP',
      path: 'get_profile_segment_counts',
      description: 'Profile counts per tag/segment. Omit tag_name to list every tag.',
      request: `{ "tag_name": "VIP" }`,
      response: `{
  "query": "VIP",
  "tags": [{ "name": "VIP", "description": "High spenders", "profile_count": 12 }]
}`
    },
    {
      title: 'get_referral_stats',
      method: 'MCP',
      path: 'get_referral_stats',
      description: 'Total referral counts and the top referring profiles (by profile id — no name lookup available). No parameters.',
      response: `{
  "total_referrals": 34,
  "unique_referrers": 12,
  "top_referrers": [{ "profile_id": 7, "referral_count": 5 }]
}`
    },
  ];

  const EndpointCard = ({ endpoint }) => (
    <Box
      p={5}
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      transition="all 0.2s"
      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
    >
      <Flex align="center" mb={3}>
        <Badge
          colorScheme={methodColors[endpoint.method]}
          px={3}
          py={1}
          borderRadius="full"
          fontWeight="bold"
          fontSize="xs"
        >
          {endpoint.method}
        </Badge>
        <Text ml={3} fontWeight="semibold" fontSize="md" color="gray.700">
          {endpoint.title}
        </Text>
      </Flex>
      <Code
        display="block"
        p={3}
        borderRadius="md"
        bg="gray.50"
        fontSize="sm"
        color="purple.600"
        fontWeight="medium"
      >
        {endpoint.path}
      </Code>
      <Text mt={3} fontSize="sm" color="gray.500">
        {endpoint.description}
      </Text>

      {(endpoint.request || endpoint.response) && (
        <Accordion allowToggle mt={4}>
          {endpoint.request && (
            <AccordionItem border="none">
              <AccordionButton
                px={3}
                py={2}
                bg="blue.50"
                borderRadius="md"
                _hover={{ bg: 'blue.100' }}
              >
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                    Request Body
                  </Badge>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={2} pt={2}>
                <Code
                  display="block"
                  p={3}
                  borderRadius="md"
                  bg="gray.800"
                  color="green.300"
                  fontSize="xs"
                  whiteSpace="pre"
                  overflowX="auto"
                >
                  {endpoint.request}
                </Code>
              </AccordionPanel>
            </AccordionItem>
          )}
          {endpoint.response && (
            <AccordionItem border="none" mt={endpoint.request ? 2 : 0}>
              <AccordionButton
                px={3}
                py={2}
                bg="green.50"
                borderRadius="md"
                _hover={{ bg: 'green.100' }}
              >
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    Response
                  </Badge>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={2} pt={2}>
                <Code
                  display="block"
                  p={3}
                  borderRadius="md"
                  bg="gray.800"
                  color="green.300"
                  fontSize="xs"
                  whiteSpace="pre"
                  overflowX="auto"
                >
                  {endpoint.response}
                </Code>
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </Box>
  );

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>API Documentation</Heading>
        <Text color="gray.500">Explore the available API endpoints for chat, authentication, and service monitoring.</Text>
      </Box>

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Chat</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {chatEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {chatEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Authentication</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {authEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {authEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Users</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {userEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {userEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Usage &amp; Discussions</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {usageEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {usageEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Insights</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            1 endpoint
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Health Check',
            method: 'GET',
            path: '/api/v1/health/',
            description: 'Check internal health of the Assistant service: database connectivity, the active LLM backend (self-hosted Ollama or the managed provider — see checks.llm.provider below), the shared api/control dependency, and chat activity.',
            response: `{
  "overall": "healthy",
  "checks": {
    "database": {"status": "healthy", "latency_ms": 1.23},
    "llm": {
      "provider": "ollama",
      "status": "healthy",
      "latency_ms": 4.5,
      "model": "qwen2.5:3b-instruct-q4_K_M",
      "models_available": ["qwen2.5:3b-instruct-q4_K_M"]
    },
    "control_api": {"status": "healthy", "latency_ms": 12.1},
    "chat": {
      "status": "healthy",
      "total_sessions": 42,
      "total_messages": 310,
      "sessions_24h": 6
    }
  }
}`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">AI Provider</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            2 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Get provider settings',
            method: 'GET',
            path: '/api/v1/llm-provider/',
            description: 'Get the current global LLM provider toggle. The API key is never returned in full — only whether one is set and a masked preview.',
            response: `{
  "provider": "managed",
  "managed_base_url": "https://inference.do-ai.run/v1",
  "managed_model": "kimi-k2-instruct",
  "managed_api_key_set": true,
  "managed_api_key_masked": "****************ab12",
  "is_managed_configured": true,
  "updated_at": "2026-08-03T12:00:00Z"
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Update provider settings',
            method: 'PUT',
            path: '/api/v1/llm-provider/',
            description: 'Switch between the self-hosted (Ollama) and managed (OpenAI-compatible) backends, or update the managed provider\'s base URL/model/API key. This is a single global setting — it takes effect for every tenant\'s next chat turn immediately. Omit managed_api_key to keep the previously-stored key. Switching to "managed" requires a base URL, model, and API key (existing or newly provided).',
            request: `{
  "provider": "managed",
  "managed_base_url": "https://inference.do-ai.run/v1",
  "managed_model": "kimi-k2-instruct",
  "managed_api_key": "sk-..."
}`,
            response: `{
  "success": true,
  "provider": "managed",
  "managed_base_url": "https://inference.do-ai.run/v1",
  "managed_model": "kimi-k2-instruct",
  "managed_api_key_set": true,
  "managed_api_key_masked": "****************ab12",
  "is_managed_configured": true,
  "updated_at": "2026-08-03T12:00:00Z"
}`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">PostgreSQL Status</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            4 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Server Status',
            method: 'GET',
            path: '/api/v1/postgres/status/',
            description: 'Get server version, uptime, database size, connection stats and configuration.',
            response: '{"success": true, "postgres": {"connected": true, "server_info": {...}, "database": {...}, "connections": {...}}}'
          }} />
          <EndpointCard endpoint={{
            title: 'List Tables',
            method: 'GET',
            path: '/api/v1/postgres/tables/?page=1&sort=size&search=',
            description: 'List tables with sizes, row counts, and vacuum stats. Supports pagination, sorting, and search.',
            response: '{"success": true, "tables": [...], "pagination": {"page": 1, "pages": 1, "total": 3}}'
          }} />
          <EndpointCard endpoint={{
            title: 'Active Queries',
            method: 'GET',
            path: '/api/v1/postgres/activity/',
            description: 'Get active queries and connections from pg_stat_activity.',
            response: '{"success": true, "activity": [{"pid": 123, "state": "active", "query": "SELECT ..."}]}'
          }} />
          <EndpointCard endpoint={{
            title: 'Execute SQL Query',
            method: 'POST',
            path: '/api/v1/postgres/query/',
            description: 'Execute a read-only SQL query against the database. Only SELECT statements are allowed.',
            request: `{
  "sql": "SELECT * FROM selwyn_assistant_chat_sessions LIMIT 10"
}`,
            response: `{
  "success": true,
  "columns": ["id", "tenant_id", "created_at", "updated_at"],
  "rows": [
    [1, 3, "2026-08-01T10:00:00", "2026-08-01T10:05:00"]
  ],
  "row_count": 1
}`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">MCP API Keys</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {mcpKeyEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {mcpKeyEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">MCP Server</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {mcpTools.length} tools
          </Badge>
        </Flex>
        <Alert status="info" borderRadius="lg" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">
            This is a separate interface for MCP-compatible clients (e.g. Claude, other agents) —
            not a plain REST endpoint like everything above. Connect over Streamable HTTP at{' '}
            <Code fontSize="sm">/mcp</Code>, sending one of two kinds of bearer token in{' '}
            <Code fontSize="sm">Authorization: Bearer &lt;token&gt;</Code>:
          </Text>
          <Text fontSize="sm" mt={2}>
            <strong>Tenant-scoped key</strong> (recommended — see the MCP API Keys page): issued for one
            specific instance, so no <Code fontSize="sm">X-Tenant-ID</Code> header is needed at all — the
            tenant is read from the key itself and any header the caller sends is ignored, so a leaked key
            can never be pointed at a different tenant.
          </Text>
          <Text fontSize="sm" mt={2}>
            <strong>Global ops token</strong> (<Code fontSize="sm">MCP_ACCESS_TOKEN</Code>, internal use only):
            can act as any tenant, so a required <Code fontSize="sm">X-Tenant-ID</Code> header states which one
            on every call.
          </Text>
          <Text fontSize="sm" mt={2}>
            Either way, the instance must have the AI assistant capability enabled, or every call returns
            403. These are the exact same tool implementations the Chat page's agent uses — same results
            either way.
          </Text>
        </Alert>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {mcpTools.map((tool, index) => (
            <EndpointCard key={index} endpoint={tool} />
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default ApiDocs;
